import { Link } from "react-router-dom";
import { Mail, Phone, Facebook, Linkedin } from "lucide-react";
import { AGS_LOGO_URL } from "@/lib/brand";
import { SERVICES } from "@/data/services";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#050B14] text-white pt-20 md:pt-28 pb-10 relative aaa-noise overflow-hidden"
    >
      {/* Top hairline accent */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#2BB6E5]/40 to-transparent" />

      {/* Subtle radial ambient at top-left for editorial depth */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[600px] h-[600px] pointer-events-none opacity-[0.08]"
        style={{
          background:
            "radial-gradient(circle, rgba(43,182,229,0.6) 0%, rgba(14,42,87,0) 60%)",
        }}
      />

      <div className="container-ags relative">
        {/* Editorial line on top of footer */}
        <div className="grid md:grid-cols-12 gap-10 pb-16 border-b border-white/10">
          <div className="md:col-span-5">
            <img
              src={AGS_LOGO_URL}
              alt="AGS Cleaning Services"
              className="h-24 md:h-28 w-auto object-contain -ml-2 brightness-0 invert"
            />

            <p className="mt-6 italic-display text-2xl md:text-3xl text-white leading-[1.25] max-w-md">
              "Same crew, same standards, same family answering the phone."
            </p>
            <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#2BB6E5]">
              — The AGS Family
            </div>

            <div className="mt-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
                Connect
              </div>
              <div className="mt-4 flex items-center gap-2">
                <a
                  href="https://www.facebook.com/profile.php?id=61591672779664"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-social-facebook"
                  aria-label="AGS on Facebook"
                  className="w-11 h-11 border border-white/15 hover:bg-[#2BB6E5] hover:text-[#050B14] hover:border-[#2BB6E5] hover:-translate-y-0.5 flex items-center justify-center transition-[transform,background-color,color,border-color] duration-300"
                >
                  <Facebook size={16} />
                </a>
                <a
                  href="https://linkedin.com/company/agscleaning"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-social-linkedin"
                  aria-label="AGS on LinkedIn"
                  className="w-11 h-11 border border-white/15 hover:bg-[#2BB6E5] hover:text-[#050B14] hover:border-[#2BB6E5] hover:-translate-y-0.5 flex items-center justify-center transition-[transform,background-color,color,border-color] duration-300"
                >
                  <Linkedin size={16} />
                </a>
              </div>
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
              01 — Services
            </div>
            <ul className="mt-6 grid grid-cols-1 gap-2.5 text-[14px] text-white/75">
              {SERVICES.map((s, i) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="inline-flex items-baseline gap-3 hover:text-white group transition-colors"
                  >
                    <span className="font-mono text-[10px] tracking-[0.18em] text-white/35 group-hover:text-[#2BB6E5]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="border-b border-transparent group-hover:border-[#2BB6E5]/60 transition-colors">
                      {s.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3 space-y-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
                02 — Reach Us
              </div>
              <ul className="mt-6 space-y-2.5 text-[14px] text-white/75">
                <li>
                  <Link to="/careers" data-testid="footer-link-careers" className="hover:text-white">Careers</Link>
                </li>
                <li>
                  <Link to="/contact" data-testid="footer-link-contact" className="hover:text-white">Contact Us</Link>
                </li>
                <li>
                  <Link to="/facility-management" className="hover:text-white">Facility Management</Link>
                </li>
                <li>
                  <Link to="/process" className="hover:text-white">Our Process</Link>
                </li>
                <li>
                  <Link to="/expertise" className="hover:text-white">Expertise</Link>
                </li>
              </ul>
              <ul className="mt-8 space-y-2.5 text-[13px] text-white/65">
                <li className="flex items-start gap-3">
                  <Phone size={13} className="mt-1 text-white/40" />
                  <a href="tel:+353873816923" className="hover:text-white" data-testid="footer-phone">+353 87 381 6923</a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={13} className="mt-1 text-white/40" />
                  <a href="mailto:info@ags-services.ie" className="hover:text-white">info@ags-services.ie</a>
                </li>
              </ul>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
                03 — Legal
              </div>
              <ul className="mt-6 space-y-2.5 text-[14px] text-white/75">
                <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-white">Privacy Notice</Link></li>
                <li><Link to="/cookies" data-testid="footer-link-cookies" className="hover:text-white">Cookie Policy</Link></li>
                <li><Link to="/accessibility" data-testid="footer-link-accessibility" className="hover:text-white">Accessibility Statement</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Compliance bottom bar */}
        <div className="mt-16 pt-12 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 font-mono text-[11px] tracking-[0.12em] text-white/50">
          <div className="uppercase">
            © AGS Service · All rights reserved
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 uppercase">
            <span>Fully Insured</span>
            <span>RAMS · SDS</span>
            <span>Family-Owned · Ireland</span>
          </div>
        </div>

        {/* Counties served — SEO + local discovery (placed at the very bottom for search engines) */}
        <div data-testid="footer-counties" className="mt-10 pt-10 border-t border-white/5">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">
            Service Areas · Counties of Ireland
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-1.5 text-[12px] text-white/45">
            {[
              "Carlow", "Cavan", "Clare", "Cork", "Donegal", "Dublin",
              "Galway", "Kerry", "Kildare", "Kilkenny", "Laois", "Leitrim",
              "Limerick", "Longford", "Louth", "Mayo", "Meath", "Monaghan",
              "Offaly", "Roscommon", "Sligo", "Tipperary", "Waterford",
              "Westmeath", "Wexford", "Wicklow",
            ].map((county) => (
              <li key={county}>
                <Link
                  to={`/areas/${county.toLowerCase()}`}
                  data-testid={`footer-county-${county.toLowerCase()}`}
                  className="hover:text-white/85 transition-colors"
                >
                  Cleaning Services {county}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
